export const formatCurrency = (
  amount: number,
  currency: string = 'USD',
  locale: string = 'en-US',
): string => {
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number.isFinite(amount) ? amount : 0);
  } catch {
    const safe = Number.isFinite(amount) ? amount : 0;
    return `$${safe.toFixed(2)}`;
  }
};

/**
 * Keep only digits and a single decimal point, clamp to 2 decimals.
 */
export const sanitizeCurrencyInput = (value: string): string => {
  const cleaned = (value || '').replace(/[^0-9.]/g, '');
  const parts = cleaned.split('.');
  if (parts.length > 2) {
    return parts[0] + '.' + parts[1];
  }
  if (parts[1] && parts[1].length > 2) {
    return parts[0] + '.' + parts[1].substring(0, 2);
  }
  return cleaned;
};

/**
 * Short month date like "Feb 23, 2024" in en-US.
 */
export const formatShortDate = (isoLike?: string): string => {
  if (!isoLike) return '';
  try {
    const d = new Date(isoLike);
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return isoLike;
  }
};

/**
 * Format like iOS getRelativeDescription()
 * - Today: h:mm AM/PM
 * - Yesterday: "Yesterday"
 * - Same year: "MMM d"
 * - Different year: "MMM d, yyyy"
 */
export const formatRelativeDate = (isoLike?: string | number | Date): string => {
  if (!isoLike) return '';
  try {
    const d = new Date(isoLike);
    const now = new Date();

    const startOfDay = (dt: Date) => new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
    const dayMs = 24 * 60 * 60 * 1000;
    const diffDays = Math.floor((startOfDay(now).getTime() - startOfDay(d).getTime()) / dayMs);

    if (diffDays === 0) {
      return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    }
    if (diffDays === 1) {
      return 'Yesterday';
    }
    if (now.getFullYear() === d.getFullYear()) {
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return String(isoLike);
  }
};

/**
 * Format like iOS section header getFullDate(): "Thu Jan 30 2025"
 */
export const formatFullHeaderDate = (isoLike?: string | number | Date): string => {
  if (!isoLike) return '';
  try {
    const d = new Date(isoLike);
    const weekday = d.toLocaleDateString('en-US', { weekday: 'short' });
    const month = d.toLocaleDateString('en-US', { month: 'short' });
    const day = d.toLocaleDateString('en-US', { day: 'numeric' });
    const year = d.getFullYear();
    return `${weekday} ${month} ${day} ${year}`;
  } catch {
    return String(isoLike);
  }
};

/**
 * Capitalize the first character and lower-case the rest.
 */
export const capitalizeFirstLetter = (str: string = ''): string => {
  return str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : '';
};

/**
 * Get Date pointing to the first day of the next month from a given date.
 */
export const getFirstOfNextMonth = (now: Date): Date => {
  return new Date(
    now.getMonth() === 11 ? now.getFullYear() + 1 : now.getFullYear(),
    (now.getMonth() + 1) % 12,
    1,
  );
};

/**
 * Format a timestamp for message bubbles.
 * Returns time in format like "3:45 PM"
 */
export const formatMessageTime = (isoLike?: string | number | Date): string => {
  if (!isoLike) return '';
  try {
    return new Date(isoLike).toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return '';
  }
};

/**
 * Format a timestamp to a date string for chat date separators.
 * Returns format like "Aug 15, 2024"
 */
export const formatChatDate = (isoLike?: string | number | Date): string => {
  if (!isoLike) return 'Unknown date';
  try {
    return new Date(isoLike).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return 'Unknown date';
  }
};

/**
 * Format a timestamp for message date grouping (returns simple date string).
 * Used for grouping messages by date.
 */
export const formatMessageDate = (isoLike?: string | number | Date): string => {
  if (!isoLike) return '';
  try {
    return new Date(isoLike).toDateString();
  } catch {
    return '';
  }
};

/**
 * Format a date header for chat windows with relative dates.
 * Returns "Today", "Yesterday", or a formatted date string.
 */
export const formatChatDateHeader = (isoLike?: string | number | Date): string => {
  if (!isoLike) return '';
  try {
    const date = new Date(isoLike);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    }
    if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    }
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      month: 'long', 
      day: 'numeric' 
    });
  } catch {
    return String(isoLike);
  }
};
